import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './CreateRoom.scss';

import cons from '../../../client/state/cons';
import navigation from '../../../client/state/navigation';
import { useSetAtom } from 'jotai';
import { openReusableContextMenu, updateRoomProcessInfo } from '../../../client/action/navigation';
import * as roomActions from '../../../client/action/room';
import { allRoomsAtom } from '../../state/room-list/roomList';
import { roomListForceRefreshAtom } from '../../state/room/roomListForceRefresh';
import { isRoomAliasAvailable, getIdServer } from '../../../util/matrixUtil';
import { getEventCords } from '../../../util/common';

import Text from '../../atoms/text/Text';
import Button from '../../atoms/button/Button';
import Toggle from '../../atoms/button/Toggle';
import IconButton from '../../atoms/button/IconButton';
import { MenuHeader, MenuItem } from '../../atoms/context-menu/ContextMenu';
import Input from '../../atoms/input/Input';
import Spinner from '../../atoms/spinner/Spinner';
import SegmentControl from '../../atoms/segmented-controls/SegmentedControls';
import Dialog from '../../molecules/dialog/Dialog';
import SettingTile from '../../molecules/setting-tile/SettingTile';

import HashPlusIC from '../../../../public/res/ic/outlined/hash-plus.svg';
import SpacePlusIC from '../../../../public/res/ic/outlined/space-plus.svg';
import HashIC from '../../../../public/res/ic/outlined/hash.svg';
import HashLockIC from '../../../../public/res/ic/outlined/hash-lock.svg';
import HashGlobeIC from '../../../../public/res/ic/outlined/hash-globe.svg';
import SpaceIC from '../../../../public/res/ic/outlined/space.svg';
import SpaceLockIC from '../../../../public/res/ic/outlined/space-lock.svg';
import SpaceGlobeIC from '../../../../public/res/ic/outlined/space-globe.svg';
import ChevronBottomIC from '../../../../public/res/ic/outlined/chevron-bottom.svg';
import CrossIC from '../../../../public/res/ic/outlined/cross.svg';
import { useRoomNavigate } from '../../hooks/useRoomNavigate';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useSetSetting } from '../../state/hooks/settings';
import { settingsAtom } from '../../state/settings';

// import { CurveAIID } from '../../utils/CurveDefaults';

function CreateRoomContent({ isSpace, parentId, onRequestClose }) {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const setAllRooms = useSetAtom(allRoomsAtom);
  const setForceRefresh = useSetAtom(roomListForceRefreshAtom);
  const [joinRule, setJoinRule] = useState(parentId ? 'restricted' : 'invite');
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [creatingError, setCreatingError] = useState(null);
  const { navigateRoom, navigateSpace } = useRoomNavigate();
  const setPeopleDrawer = useSetSetting(settingsAtom, 'isPeopleDrawer');

  const [isValidAddress, setIsValidAddress] = useState(null);
  const [addressValue, setAddressValue] = useState(undefined);
  const [roleIndex, setRoleIndex] = useState(0);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isFetchingProjects, setIsFetchingProjects] = useState(true);

  const addressRef = useRef(null);

  const userHs = getIdServer(mx.getUserId());

  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      try {
        const profile = await mx.getProfileInfo(mx.getUserId());
        if (!isMounted) return;
        const projectStr = profile.project || '';
        const projectList = projectStr.split(',').map((p) => p.trim()).filter((p) => p !== '');
        setProjects(projectList);
        if (projectList.length > 0) {
          setSelectedProject(projectList[0]);
        }
        setIsFetchingProjects(false);
      } catch (e) {
        console.error('Failed to fetch user projects', e);
        if (isMounted) setIsFetchingProjects(false);
      }
    };
    fetchProjects();
    return () => { isMounted = false; };
  }, [mx]);
/*
  async function inviteToRoom(userId) {
    if (typeof roomId === 'undefined') return;
    try {
      addUserToProc(userId);
      procUserError.delete(userId);
      updateUserProcError(getMapCopy(procUserError));

      await mx.invite(roomId, userId);

      invitedUserIds.add(userId);
      updateInvitedUserIds(new Set(Array.from(invitedUserIds)));
      deleteUserFromProc(userId);

    } catch (e) {
      deleteUserFromProc(userId);
      if (typeof e.message === 'string') procUserError.set(userId, e.message);
      else procUserError.set(userId, 'Something went wrong!');
      updateUserProcError(getMapCopy(procUserError));
    }

  }
*/
  const handleSubmit = async (evt) => {
    evt.preventDefault();
    const { target } = evt;

    if (isCreatingRoom) return;
    setIsCreatingRoom(true);
    setCreatingError(null);

    const name = target.name.value;
    let topic = ''; // target.topic.value;
    if (topic.trim() === '') topic = undefined;
    let roomAlias;
    if (joinRule === 'public') {
      roomAlias = addressRef?.current?.value;
      if (roomAlias.trim() === '') roomAlias = undefined;
    }

    const powerLevel = roleIndex === 1 ? 101 : undefined;

    try {
      const data = await roomActions.createRoom(mx, {
        name,
        topic,
        joinRule,
        alias: roomAlias,
        isEncrypted: isSpace || joinRule === 'public' ? false : isEncrypted,
        powerLevel,
        isSpace,
        parentId,
        project: selectedProject,
      });
      setIsCreatingRoom(false);
      setCreatingError(null);
      setIsValidAddress(null);
      setAddressValue(undefined);
      onRequestClose();
      if (isSpace) {
        navigateSpace(data.room_id);
      } else {
        navigateRoom(data.room_id);
        setPeopleDrawer(true);
        updateRoomProcessInfo(data.room_id);
        setAllRooms({ type: 'PUT', roomId: data.room_id });
        setForceRefresh((prev) => prev + 1);
      }
      // await mx.invite(data.room_id, CurveAIID); // TMS:

    } catch (e) {
      if (e.message === 'M_UNKNOWN: Invalid characters in room alias') {
        setCreatingError(t('CreateRoom.errors.invalid_address'));
        setIsValidAddress(false);
      } else if (e.message === 'M_ROOM_IN_USE: Room alias already taken') {
        setCreatingError(t('CreateRoom.errors.address_in_use'));
        setIsValidAddress(false);
      } else setCreatingError(e.message);
      setIsCreatingRoom(false);
    }
  };

  const validateAddress = (e) => {
    const myAddress = e.target.value;
    setIsValidAddress(null);
    setAddressValue(e.target.value);
    setCreatingError(null);

    setTimeout(async () => {
      if (myAddress !== addressRef.current.value) return;
      const roomAlias = addressRef.current.value;
      if (roomAlias === '') return;
      const roomAddress = `#${roomAlias}:${userHs}`;

      if (await isRoomAliasAvailable(mx, roomAddress)) {
        setIsValidAddress(true);
      } else {
        setIsValidAddress(false);
      }
    }, 1000);
  };

  const joinRules = ['invite', 'restricted', 'public'];
  const joinRuleText = [
    t('CreateRoom.join_rules.private'),
    t('CreateRoom.join_rules.restricted'),
    t('CreateRoom.join_rules.public'),
  ];
  const jrRoomIC = [HashLockIC, HashIC, HashGlobeIC];
  const jrSpaceIC = [SpaceLockIC, SpaceIC, SpaceGlobeIC];
  const handleJoinRule = (evt) => {
    openReusableContextMenu('bottom', getEventCords(evt, '.btn-surface'), (closeMenu) => (
      <>
        <MenuHeader>{t('CreateRoom.join_rule_visibility')}</MenuHeader>
        {joinRules.map((rule) => (
          <MenuItem
            key={rule}
            variant={rule === joinRule ? 'positive' : 'surface'}
            iconSrc={
              isSpace ? jrSpaceIC[joinRules.indexOf(rule)] : jrRoomIC[joinRules.indexOf(rule)]
            }
            onClick={() => {
              closeMenu();
              setJoinRule(rule);
            }}
            disabled={!parentId && rule === 'restricted'}
          >
            {joinRuleText[joinRules.indexOf(rule)]}
          </MenuItem>
        ))}
      </>
    ));
  };

  const handleProjectMenu = (evt) => {
    openReusableContextMenu('bottom', getEventCords(evt, '.btn-surface'), (closeMenu) => (
      <>
        <MenuHeader>{t('CreateRoom.select_project')}</MenuHeader>
        {projects.map((project) => (
          <MenuItem
            key={project}
            variant={project === selectedProject ? 'positive' : 'surface'}
            onClick={() => {
              closeMenu();
              setSelectedProject(project);
            }}
          >
            {project}
          </MenuItem>
        ))}
      </>
    ));
  };

  return (
    <div className="create-room">
      <form className="create-room__form" onSubmit={handleSubmit}>
        {projects.length > 1 && (
          <SettingTile
            title={t('CreateRoom.project')}
            options={
              <Button onClick={handleProjectMenu} iconSrc={ChevronBottomIC}>
                {selectedProject || t('CreateRoom.select_project')}
              </Button>
            }
            content={
              <Text variant="b3">{t('CreateRoom.select_project_desc')}</Text>
            }
          />
        )}
        {projects.length === 1 && (
          <SettingTile
            title={t('CreateRoom.project')}
            options={
              <Button disabled iconSrc={null}>
            {selectedProject}
          </Button>
            }
            content={
              <Text variant="b3">{t('CreateRoom.assigned_project_desc')}</Text>
            }
          />
        )}
        {!isFetchingProjects && projects.length === 0 && (
          <Text className="create-room__error" variant="b3" style={{ color: 'var(--bg-danger)', marginBottom: '16px' }}>
            {t('CreateRoom.no_project_error')}
          </Text>
        )}
        {isFetchingProjects && (
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <Spinner size="small" />
            <Text style={{ marginLeft: '8px' }}>{t('CreateRoom.fetching_projects')}</Text>
          </div>
        )}
        <div className="create-room__name-wrapper">
          <Input
            name="name"
            value={t('CreateRoom.default_name')}
            label={t(isSpace ? 'CreateRoom.space_name' : 'CreateRoom.room_name')}
            required
          />
          <Button
            disabled={isValidAddress === false || isCreatingRoom || (!isFetchingProjects && projects.length === 0)}
            iconSrc={isSpace ? SpacePlusIC : HashPlusIC}
            type="submit"
            variant="primary"
          >
            {t('CreateRoom.create')}
          </Button>
        </div>
        {isCreatingRoom && (
          <div className="create-room__loading">
            <Spinner size="small" />
            <Text>{t('CreateRoom.creating', { type: t(isSpace ? 'CreateRoom.space_lower' : 'CreateRoom.room_lower') })}</Text>
          </div>
        )}
        {typeof creatingError === 'string' && (
          <Text className="create-room__error" variant="b3">
            {creatingError}
          </Text>
        )}
      </form>
    </div>
  );
}
CreateRoomContent.defaultProps = {
  parentId: null,
};
CreateRoomContent.propTypes = {
  isSpace: PropTypes.bool.isRequired,
  parentId: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
};

function useWindowToggle() {
  const [create, setCreate] = useState(null);

  useEffect(() => {
    const handleOpen = (isSpace, parentId) => {
      setCreate({
        isSpace,
        parentId,
      });
    };
    navigation.on(cons.events.navigation.CREATE_ROOM_OPENED, handleOpen);
    return () => {
      navigation.removeListener(cons.events.navigation.CREATE_ROOM_OPENED, handleOpen);
    };
  }, []);

  const onRequestClose = () => setCreate(null);

  return [create, onRequestClose];
}

function CreateRoom() {
  const { t } = useTranslation();
  const [create, onRequestClose] = useWindowToggle();
  const { isSpace, parentId } = create ?? {};
  const mx = useMatrixClient();
  const room = mx.getRoom(parentId);

  return (
    <Dialog
      isOpen={create !== null}
      title={
        <Text variant="s1" weight="medium" primary>
          {parentId ? room?.name : t('CreateRoom.home')}
          <span style={{ color: 'var(--tc-surface-low)' }}>
            {` ${t('CreateRoom.title_suffix', { type: t(isSpace ? 'CreateRoom.space_lower' : 'CreateRoom.room_lower') })}`}
          </span>
        </Text>
      }
      contentOptions={<IconButton src={CrossIC} onClick={onRequestClose} tooltip={t('Common.close')} />}
      onRequestClose={onRequestClose}
    >
      {create ? (
        <CreateRoomContent isSpace={isSpace} parentId={parentId} onRequestClose={onRequestClose} />
      ) : (
        <div />
      )}
    </Dialog>
  );
}

export default CreateRoom;
